import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Settings.css';

const Settings = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    email_notifications: true,
    course_updates: true,
    promotional_emails: false,
    weekly_digest: true,
    dark_mode: false,
    language: 'en',
    timezone: 'UTC',
  });
  const [loading, setLoading] = useState(false);
  const [gdprLoading, setGdprLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      if (response.data) {
        setSettings((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (e) => {
    setSettings((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleExportData = async () => {
    setGdprLoading(true);
    try {
      const res = await api.get('/auth/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-lms-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: t('settings.exportFailed') });
    } finally {
      setGdprLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('settings.deleteConfirm'))) return;
    setGdprLoading(true);
    try {
      await api.delete('/auth/me');
      logout();
      navigate('/login');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('settings.deleteFailed') });
      setGdprLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/notifications/preferences', settings);
      setMessage({ type: 'success', text: t('settings.saveSuccess') });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('settings.saveFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page settings-page">
      <div className="container">
        <h1>{t('settings.title')}</h1>

        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="settings-section">
            <h2>{t('settings.notifications')}</h2>
            <p className="section-description">{t('settings.notificationsDesc')}</p>

            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.emailNotifications')}</h3>
                <p>{t('settings.emailNotificationsDesc')}</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={() => handleToggle('email_notifications')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.courseUpdates')}</h3>
                <p>{t('settings.courseUpdatesDesc')}</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.course_updates}
                  onChange={() => handleToggle('course_updates')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.weeklyDigest')}</h3>
                <p>{t('settings.weeklyDigestDesc')}</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.weekly_digest}
                  onChange={() => handleToggle('weekly_digest')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.promotionalEmails')}</h3>
                <p>{t('settings.promotionalEmailsDesc')}</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.promotional_emails}
                  onChange={() => handleToggle('promotional_emails')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h2>{t('settings.displayPrefs')}</h2>
            <p className="section-description">{t('settings.displayPrefsDesc')}</p>

            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.darkMode')}</h3>
                <p>{t('settings.darkModeDesc')}</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.dark_mode}
                  onChange={() => handleToggle('dark_mode')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item select-item">
              <div className="setting-info">
                <h3>{t('settings.language')}</h3>
                <p>{t('settings.languageDesc')}</p>
              </div>
              <select
                name="language"
                value={settings.language}
                onChange={handleChange}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="ar">Arabic</option>
                <option value="ur">Urdu</option>
              </select>
            </div>

            <div className="setting-item select-item">
              <div className="setting-info">
                <h3>{t('settings.timezone')}</h3>
                <p>{t('settings.timezoneDesc')}</p>
              </div>
              <select
                name="timezone"
                value={settings.timezone}
                onChange={handleChange}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Karachi">Karachi (PKT)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h2>{t('settings.privacySection')}</h2>
            <p className="section-description">{t('settings.privacyDesc')}</p>

            <div className="privacy-options">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleExportData}
                disabled={gdprLoading}
              >
                {gdprLoading ? t('settings.exporting') : t('settings.exportData')}
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteAccount}
                disabled={gdprLoading}
              >
                {t('settings.deleteAccount')}
              </button>
            </div>
          </div>

          <div className="settings-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? t('settings.saving') : t('settings.saveSettings')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
